/**
 * ScreenReaderFeature - Enhances page for screen reader compatibility
 * Adds ARIA attributes, improves semantic structure, and shows visual indicators
 */
import BaseFeature from './BaseFeature.js';

export default class ScreenReaderFeature extends BaseFeature {
    enable() {
        super.enable();
        this._enhanceAria();
        this._addVisualIndicators();
        this.addBodyClass('dhA11y-screen-reader');
    }

    disable() {
        super.disable();
        this._removeEnhancements();
        this._removeVisualIndicators();
        this.removeBodyClass('dhA11y-screen-reader');
    }

    /**
     * Enhance ARIA attributes across the page
     */
    _enhanceAria() {
        // Add role="main" if missing
        const main = document.querySelector('main, [role="main"], #content, .content');
        if (main && !main.getAttribute('role')) {
            main.setAttribute('role', 'main');
            main.dataset.dhA11yEnhanced = 'true';
        }

        // Add aria-label to images without alt
        document.querySelectorAll('img:not([alt])').forEach(img => {
            img.setAttribute('alt', '');
            img.dataset.dhA11yEnhanced = 'true';
        });

        // Enhance links that open in new window
        document.querySelectorAll('a[target="_blank"]').forEach(link => {
            if (!link.getAttribute('aria-label')) {
                const text = link.textContent.trim();
                if (text) {
                    link.setAttribute('aria-label', text + ' (opens in new window)');
                    link.dataset.dhA11yEnhanced = 'true';
                }
            }
        });

        // Add aria-current to active navigation items
        document.querySelectorAll('nav a.active, nav .current, .nav .active > a').forEach(el => {
            if (!el.getAttribute('aria-current')) {
                el.setAttribute('aria-current', 'page');
                el.dataset.dhA11yEnhanced = 'true';
            }
        });

        // Ensure form labels
        document.querySelectorAll('input:not([aria-label]):not([id])').forEach(input => {
            const placeholder = input.getAttribute('placeholder');
            if (placeholder) {
                input.setAttribute('aria-label', placeholder);
                input.dataset.dhA11yEnhanced = 'true';
            }
        });

        // Add tabindex to interactive elements missing it
        document.querySelectorAll('[onclick]:not([tabindex]):not(a):not(button)').forEach(el => {
            el.setAttribute('tabindex', '0');
            el.setAttribute('role', 'button');
            el.dataset.dhA11yEnhanced = 'true';
        });
    }

    /**
     * Add visual indicators showing ARIA landmarks and roles
     */
    _addVisualIndicators() {
        // Show landmark labels
        const landmarks = [
            { selector: 'nav, [role="navigation"]', label: 'Navigation' },
            { selector: 'main, [role="main"]', label: 'Main Content' },
            { selector: 'header, [role="banner"]', label: 'Banner' },
            { selector: 'footer, [role="contentinfo"]', label: 'Footer' },
            { selector: 'aside, [role="complementary"]', label: 'Aside' },
            { selector: 'form, [role="form"]', label: 'Form' },
            { selector: '[role="search"]', label: 'Search' }
        ];

        landmarks.forEach(lm => {
            document.querySelectorAll(lm.selector).forEach(el => {
                if (el.closest('#accessibility-widget, .typo3-adminPanel')) return;
                const badge = document.createElement('span');
                badge.className = 'dhA11y-sr-badge';
                badge.textContent = lm.label;
                badge.setAttribute('aria-hidden', 'true');
                el.style.position = el.style.position || 'relative';
                el.appendChild(badge);
            });
        });
    }

    /**
     * Remove visual indicators
     */
    _removeVisualIndicators() {
        document.querySelectorAll('.dhA11y-sr-badge').forEach(el => el.remove());
    }

    /**
     * Remove enhancements
     */
    _removeEnhancements() {
        document.querySelectorAll('[data-dh-a11y-enhanced]').forEach(el => {
            if (el.tagName === 'IMG' && el.getAttribute('alt') === '') {
                el.removeAttribute('alt');
            }
            if (el.dataset.dhA11yEnhanced === 'true') {
                el.removeAttribute('aria-label');
                el.removeAttribute('aria-current');
                el.removeAttribute('tabindex');
                el.removeAttribute('role');
            }
            el.removeAttribute('data-dh-a11y-enhanced');
        });
    }
}
